/**
 * FileSource — the pluggable "where do the files live" module.
 *
 * The editor and board never know whether files come from browser memory, a
 * Studio backend, or a public repo on the internet — they read the
 * `WorkspaceStore`, and a `FileSource` is what seeds and (optionally) persists
 * that store. Swapping the source is how one composition differs from another:
 *
 *   - memory / localStorage → the free Viewer (scratch, quick-draft)
 *   - readonly remote        → "open this public repo" (no write-back)
 *   - server                 → Studio's per-user drafts (later phase)
 *
 * Readonly is a property of the SOURCE, not the editor. A readonly source can
 * still be edited locally to experiment — those edits live in the store as
 * dirty overlay against the source's pristine `base`, with per-file revert.
 * Readonly only means "never written back to the source".
 */
import type { TextEdit } from "@archlang/engine";
import type { Vfs } from "@archlang/viewer-core";
import type { Assets } from "../../workspaceAssets.js";

/** The persistable shape of a workspace: the files plus which one is open.
 *  `assets` carries binary files (images) a doc references — optional, since
 *  most sources are text-only. */
export interface WorkspaceSnapshot {
  readonly files: Vfs;
  readonly activeFile: string | null;
  readonly assets?: Assets;
  /** Files open as editor tabs (a subset of `files`; the active one is always
   *  included). Persisted so the editor's tab strip survives a reload. */
  readonly openTabs?: readonly string[];
}

export interface FileSourceCapabilities {
  /** Can't be written back to (e.g. a public repo). Local edits still allowed
   *  (they live in the store as dirty overlay). The granular write/create/
   *  remove/rename ABILITY is expressed by which methods the source implements,
   *  so there's no separate canCreate/canDelete/canRename — the binder checks
   *  method presence and falls back when one is missing. */
  readonly readonly: boolean;
}

/** A single external file change (server push / collab / watch). */
export interface FileChange {
  readonly path: string;
  /** `null` = the file was removed. */
  readonly content: string | null;
}

export interface FileSource {
  /** Human-readable origin, shown in chrome (e.g. "scratch", a repo name). */
  readonly label: string;
  readonly capabilities: FileSourceCapabilities;

  /** Read the initial workspace. May be async (remote/server) or sync (memory). */
  load(): WorkspaceSnapshot | null | Promise<WorkspaceSnapshot | null>;

  // --- write-back (omitted / no-op when readonly) --------------------------
  /** Persist one file's content. Called debounced by the binder. */
  write?(path: string, content: string): void | Promise<void>;
  /**
   * Persist one file as the SPAN-ANCHORED edits that produced it, when the
   * source can splice rather than overwrite — an IDE host applying them to its
   * own live buffer keeps that buffer's undo stack, cursor, and dirty state.
   * Offsets are UTF-16 against the file's text BEFORE the edits.
   *
   * Implementing this opts the source out of the binder's content debounce:
   * offsets are only valid against the text they were computed on, so
   * coalescing successive edit batches across a window would splice at moved
   * positions. Sources implementing it get every batch, promptly and in order.
   * Omit it and the binder falls back to debounced whole-content `write`.
   */
  writeEdits?(path: string, edits: readonly TextEdit[]): void | Promise<void>;
  create?(path: string, content: string): void | Promise<void>;
  remove?(path: string): void | Promise<void>;
  rename?(from: string, to: string): void | Promise<void>;

  /** Subscribe to external changes (server SSE, collab). Returns unsubscribe. */
  watch?(onChange: (change: FileChange) => void): () => void;
}

/** Capability preset for a writable source. */
export const MUTABLE: FileSourceCapabilities = { readonly: false };

/** Capability preset for a read-only source (local experiments still allowed). */
export const READONLY: FileSourceCapabilities = { readonly: true };

/** Workspace label from a root path — its basename. Shared by every transport
 *  leg (HTTP server, host bridge) so the two can't drift on how a root names
 *  the workspace. */
export function basenameLabel(root: string, fallback: string): string {
  return root.split(/[/\\]/).filter(Boolean).pop() ?? fallback;
}
